import { SidePanelContext } from "components/SidePanel/SidePanelContext";
import classNames from "classnames";
import React, { useContext } from "react";
import { FormSection, InPageEditableSectionField, Section, TwoColumnInPageEditableSectionField } from "../types";
import { InPageEditableFieldSidePanelNarrow, InPageEditableFieldSidePanelWide } from "./InPageEditableFieldSidePanel";

interface SidePanelSectionTemplateProps {
  sections: Array<Section>;
  isInPageEditable: boolean;
  compact?: boolean;
  displayEmptyValues?: boolean;
}

type SidePanelField = InPageEditableSectionField | TwoColumnInPageEditableSectionField;

const isTwoColumnField = (field: SidePanelField): field is TwoColumnInPageEditableSectionField =>
  "left" in field && "right" in field;

// A two-column field collapses to one regular field per column, keeping the shared label.
const toColumnField = (field: TwoColumnInPageEditableSectionField, column: "left" | "right"): InPageEditableSectionField => ({
  label: field.label,
  ...field[column],
  key: field[column].key,
});

export const SidePanelSectionTemplate = (props: SidePanelSectionTemplateProps) => {
  const { layoutType } = useContext(SidePanelContext);

  const isFieldHidden = (field: SidePanelField): boolean => {
    if (field.hidden) return true;
    if (!props.isInPageEditable && field.isEmpty && !props.displayEmptyValues) return true;

    return false;
  }

  const visibleFields = (section: FormSection) => section.fields.filter(field => !isFieldHidden(field));

  const visibleSections = props.sections
    .filter(section => !section.hidden)
    .filter(section => section.type === "custom" || visibleFields(section).length > 0);

  const renderHeader = (section: FormSection) => {
    if (section.hideTitle || !section.title) return null;

    return <h3 className="side-panel-section-header">{section.title}</h3>;
  }

  const renderNarrowList = (fields: Array<InPageEditableSectionField>) =>
    <div className="side-panel-section-template-narrow-list">
      {fields.map(field => <InPageEditableFieldSidePanelNarrow key={field.key} compact={props.compact} field={field} />)}
    </div>;

  const renderSection = (section: Section) => {
    // Custom sections bring their own design, so they get no section styling.
    if (section.type === "custom") {
      return <div className="side-panel-section-custom" key={section.key}>
        {section.render()}
      </div>;
    }

    const fields = visibleFields(section);

    // Narrow layout has no room for side by side columns, so a two-column section
    // becomes one section per column (the column header rows carry the titles).
    // Fields that are not two-column belong to neither, so they get their own section.
    if (layoutType === "narrow" && fields.some(isTwoColumnField)) {
      const twoColumnFields = fields.filter(isTwoColumnField);
      const singleColumnFields = fields.filter((field): field is InPageEditableSectionField => !isTwoColumnField(field));

      return <React.Fragment key={section.key}>
        <div className="side-panel-section">
          {renderHeader(section)}
          {renderNarrowList(twoColumnFields.map(field => toColumnField(field, "left")))}
        </div>
        <div className="side-panel-section">
          {renderNarrowList(twoColumnFields.map(field => toColumnField(field, "right")))}
        </div>
        {singleColumnFields.length > 0 && <div className="side-panel-section">
          {renderNarrowList(singleColumnFields)}
        </div>}
      </React.Fragment>;
    }

    if (layoutType === "narrow") {
      return <div className="side-panel-section" key={section.key}>
        {renderHeader(section)}
        {renderNarrowList(fields.filter((field): field is InPageEditableSectionField => !isTwoColumnField(field)))}
      </div>;
    }

    // Wide layout: the section is a subgrid (see scss) so its label/value cells
    // align to the column tracks shared by every section.
    return <div className="side-panel-section" key={section.key}>
      {renderHeader(section)}
      {fields.map(field => <InPageEditableFieldSidePanelWide key={field.key} compact={props.compact} field={field} />)}
    </div>;
  }

  return <div className={classNames("side-panel-section-template", {
    "side-panel-section-template-wide": layoutType !== "narrow"
  })}>
    {visibleSections.map(renderSection)}
  </div>;
}
